import { memo, useMemo } from 'react';
import classnames from 'classnames/bind';
import { Text, PortalTooltip } from '@wildberries/ui-kit';
import { FontSizeType } from '@wildberries/ui-kit/lib/text/types';
import i18next from 'i18next';
import { TTableRow } from '@/pages/supplies-management/_components/table/types';
import { CHOOSE_DATE_PAGE_TRANSLATIONS } from '@/pages/supplies-management/_constants/translations';
import { PreLoader } from '@/pages/supplies-management/_components/pre-loader';
import { BOX_TYPE_IDS } from '@/pages/supplies-management/_constants';
import { getFormattedDisplayCost } from '../cost-table-cell/_utils/get-fornatted-display-cost';
import {
  PAID_COEFFICIENT_COLOR,
  FREE_COEFFICIENT_COLOR,
  PAID_COEFFICIENT_BACKGROUND_COLOR,
  FREE_COEFFICIENT_BACKGROUND_COLOR,
  UNLIMITED_COEFFICIENT,
} from './_constants';
import { CoefficientIcon } from './_utils/coefficient-icon';
import { getDescription } from './_utils/get-description';
import styles from './index.module.scss';

const BLOCK_NAME = 'Coefficient-table-cell';
const cn = classnames.bind(styles);

type TProps = {
  row: TTableRow;
  isLoading?: boolean;
  textSize?: FontSizeType;
  isPalette?: boolean;
  costForPalette?: number;
};

export const CoefficientTableCell = memo(
  ({
    row: {
      paidAcceptanceCoefficient,
      short = false,
      boxTypeId,
      costPaletteRow,
    },
    isLoading = false,
    textSize = 'h4',
    isPalette,
    costForPalette,
  }: TProps) => {
    const isPaletteType: boolean = useMemo(
      () => isPalette || boxTypeId === BOX_TYPE_IDS.palette,
      [boxTypeId, isPalette],
    );

    const costPalette = useMemo(
      () => costForPalette || costPaletteRow,
      [costPaletteRow, costForPalette],
    );

    const coefficientColor = useMemo(() => {
      return paidAcceptanceCoefficient
        ? PAID_COEFFICIENT_COLOR
        : FREE_COEFFICIENT_COLOR;
    }, [paidAcceptanceCoefficient]);

    const coefficientColorBackground = useMemo(() => {
      return paidAcceptanceCoefficient
        ? PAID_COEFFICIENT_BACKGROUND_COLOR
        : FREE_COEFFICIENT_BACKGROUND_COLOR;
    }, [paidAcceptanceCoefficient]);

    const coefficientText = useMemo(() => {
      return paidAcceptanceCoefficient
        ? `x${paidAcceptanceCoefficient}`
        : i18next.t(CHOOSE_DATE_PAGE_TRANSLATIONS.freeAcceptance);
    }, [paidAcceptanceCoefficient]);

    const coefficientTextColor = useMemo(() => {
      return paidAcceptanceCoefficient ? 'red' : 'successTextColor';
    }, [paidAcceptanceCoefficient]);

    const cost = useMemo(
      () => getFormattedDisplayCost(costPalette * paidAcceptanceCoefficient),
      [costPalette, paidAcceptanceCoefficient],
    );

    const description = useMemo(() => {
      return getDescription({
        paidAcceptanceCoefficient,
        costForPalette: cost,
        isPalette: isPaletteType,
      });
    }, [paidAcceptanceCoefficient, cost, isPaletteType]);

    const isUnlimited = useMemo(() => {
      return paidAcceptanceCoefficient === UNLIMITED_COEFFICIENT && !isLoading;
    }, [paidAcceptanceCoefficient, isLoading]);

    const isOldSupply = useMemo(() => {
      return paidAcceptanceCoefficient === null;
    }, [paidAcceptanceCoefficient]);

    const isSuccess = useMemo(() => {
      return !isUnlimited && !isLoading && !isOldSupply;
    }, [isLoading, isUnlimited, isOldSupply]);

    const displayCoefficient = useMemo(() => isSuccess, [isSuccess]);

    return (
      <div className={cn(BLOCK_NAME)}>
        {isLoading && <PreLoader isLoading />}
        {displayCoefficient && (
          <PortalTooltip position="right" text={description}>
            <div className={cn(`${BLOCK_NAME}__coefficient-block`)}>
              <div className={cn(`${BLOCK_NAME}__coefficient-svg`)}>
                <CoefficientIcon
                  coefficientColor={coefficientColor}
                  coefficientColorBackground={coefficientColorBackground}
                />
              </div>

              <div className={cn(`${BLOCK_NAME}__coefficient-text`)}>
                <Text
                  color={coefficientTextColor}
                  size={textSize}
                  text={coefficientText}
                />
              </div>
            </div>
          </PortalTooltip>
        )}
        {isUnlimited && (
          <Text
            color="normalGrey"
            size="h5"
            text={i18next.t(CHOOSE_DATE_PAGE_TRANSLATIONS.unlimitedCoefficient)}
          />
        )}
        {isOldSupply && !short && <Text text="-" />}
      </div>
    );
  },
);
